"use client";

import { useRouter } from "nextjs-toploader/app";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  AlertCircle,
  Calculator,
  CheckCircle2,
  Coins,
  Wallet,
  ClipboardCheck,
  Clock,
  DollarSign,
  ExternalLink,
  Eye,
  FileText,
  History,
  Loader2,
  PictureInPicture,
  RefreshCw,
  Rocket,
  Search,
  Settings2,
  TrendingUp,
  Users,
  X,
  XCircle,
} from "lucide-react";
import InsuraBotIcon from "@/app/(authenticated)/ai-calling/_modules/components/InsuraBotIcon";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/app/_module/components/ui/card";
import { Button } from "@/app/_module/components/ui/button";
import { Input } from "@/app/_module/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/app/_module/components/ui/dialog";
import { MetricCard } from "@/app/_module/components/MetricCard";
import { useFeatureFlag } from "@/app/_module/hooks/useFeatureFlag";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { techficientService } from "@/app/_module/services/techficientService";
import { InsuraBot } from "@/app/_module/components/InsuraBot";
import {
  useEnrollmentFloater,
  EAPP_POPUP_CLOSED_EVENT,
} from "@/app/_module/contexts/EnrollmentFloaterContext";
import CommissionBadge from "./_modules/components/CommissionBadge";
import { leadsService } from "@/app/(authenticated)/leads/_modules/services/services";
import { QuoteForm, leadToPreset } from "./_modules/components/QuoteForm";
import { CarrierLogo } from "@/app/_module/components/CarrierLogo";
import { type CustomDateRange } from "@/app/(authenticated)/agency-dashboard/_module/components/agency/DateRangeSelector";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/app/_module/components/ui/popover";
import { Calendar as CalendarComponent } from "@/app/_module/components/ui/calendar";
import {
  format,
  subDays,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  startOfYear,
} from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { QuoteEnrollOnboarding } from "./_modules/components/Onboarding/QuoteEnrollOnboarding";
import { ManageProfile } from "./_modules/components/ManageProfile/ManageProfile";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";

// Scoped per-user so a shared browser (or logout → login as another agent)
// still shows each agent their own first-run welcome exactly once.
const onboardingKeyFor = (userId?: string) =>
  `quoteEnroll.onboardedAt.${userId || "anon"}`;

export default function QuoteEnrollPage() {
  const router = useRouter();
  const { enabled, isLoading: flagLoading } = useFeatureFlag("quoteAndEnroll");
  const { user, refetchUser, isInitializing, profileLoadFailed } = useUserContext();
  // Lead currently selected for an inline quote form. Picking a lead from the
  // search reveals a pre-filled QuoteForm right below — no modal, no chat
  // round-trip. `null` = no lead picked (form hidden).
  const [selectedLead, setSelectedLead] = useState<any | null>(null);
  // Re-quote flow: a Quote-history click overlays that quote's parameters on
  // top of the lead preset and auto-runs, so the agent lands straight on
  // fresh results with Apply buttons — no re-driving the form. The token
  // keys the QuoteForm so each history click remounts it cleanly.
  const [historyPreset, setHistoryPreset] = useState<any | null>(null);
  const [historyRunToken, setHistoryRunToken] = useState(0);
  // InsuraBot lives alongside the form — the agent picks how they want to
  // quote (form here, chat in the bot) on a per-lead basis. `insurabotLead`
  // is independent of `selectedLead` so the form and the bot can be open for
  // different leads at the same time (or one without the other).
  const [insurabotOpen, setInsurabotOpen] = useState(false);
  const [insurabotLead, setInsurabotLead] = useState<any | null>(null);
  const [leadQuery, setLeadQuery] = useState("");
  // Manage-profile modal — agent edits state licenses / writing numbers
  // after onboarding without leaving the page.
  const [manageOpen, setManageOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<
    "all" | "in_progress" | "submitted" | "approved" | "declined"
  >("all");
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [quoteHistoryOpen, setQuoteHistoryOpen] = useState(false);
  const [historySearch, setHistorySearch] = useState("");
  const [caseSearch, setCaseSearch] = useState("");
  // Date-range filter for the KPI stats — same control + presets as the
  // main Dashboard, defaults to THIS MONTH. Scopes every metric card.
  const [statsRange, setStatsRange] = useState<string>("this-month");
  const [statsCustomRange, setStatsCustomRange] = useState<
    CustomDateRange | undefined
  >(undefined);
  const [statsCalendarRange, setStatsCalendarRange] = useState<
    import("react-day-picker").DateRange | undefined
  >(undefined);
  const [statsCalendarOpen, setStatsCalendarOpen] = useState(false);
  const floater = useEnrollmentFloater();
  const queryClient = useQueryClient();

  // Open a submitted/terminal case read-only using its STORED launch URL
  // (its initiate-time token still loads the status/completion screen;
  // /app/resume 400s on these). techf.com → embed, carrier URL → popup.
  const viewCase = (c: any) => {
    if (!c.launchUrl) return;
    if (
      floater.popupOpen &&
      floater.popupSession?.caseId === c.techficientCaseId
    ) {
      floater.focusPopup();
      return;
    }
    let host = "";
    try {
      host = new URL(c.launchUrl).hostname;
    } catch {
      /* ignore */
    }
    floater.launch({
      caseId: c.techficientCaseId,
      launchUrl: c.launchUrl,
      carrier: c.carrierName,
      product: c.productName,
      launchMode: /(^|\.)techf\.com$/i.test(host) ? "iframe" : "popup",
    });
  };

  // Manual "Sync now" — bypasses the frontend's 30s poll cadence AND the
  // backend's 45-min reactive gate. Rate-limited server-side to 1/min per
  // tenant. On skipped=true (backend already synced recently), we still
  // invalidate the cache since a fresh DB read is basically free.
  const syncNow = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      await techficientService.syncCasesNow();
    } catch {
      /* backend already retries on 429; swallow so the UI doesn't nag */
    } finally {
      await queryClient.invalidateQueries({
        queryKey: ["techficient", "cases"],
      });
      setSyncing(false);
    }
  };

  useEffect(() => {
    if (!flagLoading && !enabled) router.replace(typeof window !== "undefined" && window.location.pathname.startsWith("/m") ? "/m/dashboard" : "/dashboard");
  }, [enabled, flagLoading, router]);

  // When a popup-mode eApp window closes (agent finished — or abandoned —
  // the carrier's drop-ticket flow), refetch the case ledger so the status
  // pill and progress bar reflect whatever the carrier now reports.
  useEffect(() => {
    const onPopupClosed = () => {
      queryClient.invalidateQueries({ queryKey: ["techficient", "cases"] });
    };
    window.addEventListener(EAPP_POPUP_CLOSED_EVENT, onPopupClosed);
    return () =>
      window.removeEventListener(EAPP_POPUP_CLOSED_EVENT, onPopupClosed);
  }, [queryClient]);

  // When the embedded application opens (Apply / Resume / chat link), jump
  // ALL the way to the top of the page where the iframe card renders —
  // agents click those buttons from the cases list far below and the eApp
  // was opening off-screen (reported on Windows, applies everywhere).
  // Belt-and-suspenders: scroll the window AND the nearest scroll container.
  useEffect(() => {
    if (!(floater.open && floater.mode === "modal" && floater.session?.launchUrl))
      return;
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
      const anchor = document.getElementById("enrollment-embed-anchor");
      anchor?.scrollIntoView({ behavior: "smooth", block: "start" });
      // Some layouts scroll an inner <main> instead of the window — walk up
      // from the anchor and reset any scrolled ancestor too.
      let el: HTMLElement | null = anchor?.parentElement || null;
      while (el) {
        if (el.scrollTop > 0) el.scrollTo({ top: 0, behavior: "smooth" });
        el = el.parentElement;
      }
    });
  }, [floater.open, floater.mode, floater.session?.launchUrl]);

  // Seen is marked when the modal opens (gate below), so closing just closes.
  const dismissOnboarding = () => setOnboardingOpen(false);

  // Remembered on the ACCOUNT (appPreferences), not just this browser — the
  // localStorage-only flag came back on every other browser, device, the app,
  // another domain, or after storage was cleared. localStorage stays as a copy.
  const markWelcomeSeen = (userId: string, at = new Date().toISOString()) => {
    try {
      window.localStorage.setItem(onboardingKeyFor(userId), at);
    } catch { /* private mode / quota — the account flag still holds */ }
    refetchUser((prev: any) => ({
      ...prev,
      user: {
        ...prev.user,
        appPreferences: { ...(prev.user?.appPreferences || {}), quoteEnrollWelcomeSeenAt: at },
      },
    }));
    api
      .patch({
        url: getApiUrl("/users/profile/app-preferences"),
        body: { quoteEnrollWelcomeSeenAt: at },
        auth: true,
      })
      .catch(() => {});
  };

  const { data: eligibility, isLoading: eligibilityLoading } = useQuery({
    queryKey: ["techficient", "eligibility"],
    queryFn: () => techficientService.getEligibility(),
    enabled,
  });

  // Show the welcome modal at most ONCE per agent, ever. The per-mount ref
  // stops a dependency re-run (e.g. eligibility refetch) re-triggering it.
  const onboardingCheckedRef = useRef(false);
  useEffect(() => {
    if (!enabled || flagLoading || eligibilityLoading) return;
    // The account flag arrives with the profile — decide only once it's loaded.
    if (isInitializing || profileLoadFailed) return;
    if (!eligibility?.provisioned) return;
    if (typeof window === "undefined") return;
    if (onboardingCheckedRef.current) return;
    const me = user?.user as any;
    const userId = me?.id;
    if (!userId) return;
    onboardingCheckedRef.current = true;
    if (me?.appPreferences?.quoteEnrollWelcomeSeenAt) return;
    let seenHere: string | null = null;
    try {
      seenHere = window.localStorage.getItem(onboardingKeyFor(userId));
    } catch { /* storage blocked */ }
    if (seenHere) {
      // Seen in this browser before the account flag existed — carry it over.
      markWelcomeSeen(userId, seenHere);
      return;
    }
    // Mark seen the MOMENT we show it — not on dismiss. Otherwise navigating
    // away with the modal open (no onOpenChange fires) leaves the flag unset.
    markWelcomeSeen(userId);
    setOnboardingOpen(true);
  }, [enabled, flagLoading, eligibilityLoading, isInitializing, profileLoadFailed, eligibility?.provisioned, user]);

  const { data: cases = [], isLoading: casesLoading } = useQuery({
    queryKey: ["techficient", "cases"],
    queryFn: () => techficientService.listCases({}),
    enabled,
    refetchInterval: 30_000,
  });

  // Commission ledger + balance rollup — drives the "Commissions" KPI strip
  // and downstream chargeback warnings on individual case rows.
  const { data: commissionsData } = useQuery({
    queryKey: ["techficient", "commissions", "mine"],
    queryFn: () => techficientService.getMyCommissions(),
    enabled,
    refetchInterval: 60_000,
  });

  // Quote history — the agent's own recent quotes (all leads + standalone).
  // Only fetched when the panel is open so we don't pay for it on every visit.
  const { data: quoteHistory = [], isLoading: quoteHistoryLoading } = useQuery({
    queryKey: ["techficient", "quotes", "mine"],
    queryFn: () => techficientService.listMyQuotes(),
    enabled: enabled && quoteHistoryOpen,
  });

  const trimmedQuery = leadQuery.trim();
  const { data: leadsSearch, isFetching: leadsLoading } = useQuery({
    queryKey: ["leads", "quote-enroll-search", trimmedQuery],
    queryFn: () =>
      leadsService.searchLeads({
        page: 1,
        limit: 6,
        query: trimmedQuery,
      } as any),
    enabled: enabled && trimmedQuery.length >= 2,
    placeholderData: keepPreviousData,
  });
  // Response shape from leadsService.searchLeads (after handleApiCall + its
  // own .data unwrap) is `{ meta, items: Lead[] }`. Belt-and-suspenders for
  // a few alternative shapes just in case the service helper changes.
  const leads: Array<any> = Array.isArray(leadsSearch)
    ? (leadsSearch as any[])
    : (leadsSearch as any)?.items ||
      (leadsSearch as any)?.data?.items ||
      (leadsSearch as any)?.leads ||
      (leadsSearch as any)?.data?.leads ||
      [];

  // Resolve the selected preset / custom range into an inclusive
  // {from, to} window + a human label for the metric subtitles. Mirrors
  // the agency dashboard's presets (6mo / YTD / 1yr / custom).
  const statsWindow = useMemo((): {
    from: Date;
    to: Date;
    label: string;
  } => {
    const now = new Date();
    const eod = (d: Date) => {
      const x = new Date(d);
      x.setHours(23, 59, 59, 999);
      return x;
    };
    const sod = (d: Date) => {
      const x = new Date(d);
      x.setHours(0, 0, 0, 0);
      return x;
    };
    switch (statsRange) {
      case "all-time":
        return { from: new Date(0), to: eod(now), label: "All time" };
      case "last-month":
        return {
          from: startOfMonth(subMonths(now, 1)),
          to: endOfMonth(subMonths(now, 1)),
          label: "Last month",
        };
      case "this-week":
        return {
          from: startOfWeek(now, { weekStartsOn: 0 }),
          to: endOfWeek(now, { weekStartsOn: 0 }),
          label: "This week",
        };
      case "last-week":
        return {
          from: startOfWeek(subDays(now, 7), { weekStartsOn: 0 }),
          to: endOfWeek(subDays(now, 7), { weekStartsOn: 0 }),
          label: "Last week",
        };
      case "custom":
        if (statsCustomRange?.from && statsCustomRange?.to) {
          const fmt = (d: Date) =>
            d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
          return {
            from: sod(statsCustomRange.from),
            to: eod(statsCustomRange.to),
            label: `${fmt(statsCustomRange.from)} – ${fmt(statsCustomRange.to)}`,
          };
        }
        return { from: new Date(0), to: eod(now), label: "All time" };
      case "this-month":
      default:
        return {
          from: startOfMonth(now),
          to: endOfMonth(now),
          label: "This month",
        };
    }
  }, [statsRange, statsCustomRange]);

  const kpis = useMemo(() => {
    const k = {
      total: 0,
      inProgress: 0,
      approvedThisMonth: 0,
      premiumLocked: 0,
    };
    const { from, to } = statsWindow;
    const inWindow = (raw: any) => {
      if (!raw) return false;
      const d = new Date(raw);
      return !Number.isNaN(d.getTime()) && d >= from && d <= to;
    };
    for (const c of cases as any[]) {
      const bucket = caseBucket(c);
      // "Started" = when the application was created (our row / techf).
      const started = c.createdAt || c.raw?.createdOn;
      // Last activity = when it moved (approval, submission…).
      const activity = c.techficientUpdatedOn || c.updatedAt || c.createdAt;
      if (inWindow(started)) {
        k.total++;
        if (bucket === "in_progress" || bucket === "submitted") k.inProgress++;
      }
      if (bucket === "approved" && inWindow(activity)) {
        k.approvedThisMonth++;
        // Annualized premium (production), fall back to modal × 12.
        const annualized =
          Number(c.annualizedPremium) ||
          Number(c.approvedModalPremium || c.appliedModalPremium || 0) * 12;
        k.premiumLocked += annualized || 0;
      }
    }
    return k;
  }, [cases, statsWindow]);

  // Commission balance scoped to the same window (Techficient sends no
  // commission data — we compute it from the ledger items' dates).
  const commissionBalance = useMemo(() => {
    const items: any[] = (commissionsData as any)?.items || [];
    const { from, to } = statsWindow;
    const inWindow = (raw: any) => {
      if (!raw) return false;
      const d = new Date(raw);
      return !Number.isNaN(d.getTime()) && d >= from && d <= to;
    };
    let advanced = 0,
      earned = 0,
      chargedBack = 0;
    for (const it of items) {
      const adv = Number(it.advancedAmount) || 0;
      const cb = Number(it.chargedBackAmount) || 0;
      if (it.status === "advanced" && inWindow(it.advancedAt)) advanced += adv;
      else if (it.status === "earned" && inWindow(it.earnedAt)) earned += adv;
      else if (it.status === "charged_back" && inWindow(it.chargedBackAt))
        chargedBack += cb;
    }
    // Fall back to the server rollup when there are no dated items (older
    // rows without timestamps) so the cards aren't blank.
    if (!items.length && (commissionsData as any)?.balance) {
      return (commissionsData as any).balance;
    }
    return { advanced, earned, chargedBack, net: advanced + earned - chargedBack };
  }, [commissionsData, statsWindow]);

  const filteredCases = useMemo(() => {
    const byStatus =
      statusFilter === "all"
        ? cases
        : cases.filter((c) => caseBucket(c) === statusFilter);
    const q = caseSearch.trim().toLowerCase();
    if (!q) return byStatus;
    return byStatus.filter((c: any) =>
      [
        c.leadName,
        c.carrierName,
        c.productName,
        c.productCode,
        c.caseStatusName,
        c.policyNumber,
        c.appliedFaceAmount != null ? String(c.appliedFaceAmount) : null,
      ]
        .filter(Boolean)
        .some((f: string) => String(f).toLowerCase().includes(q)),
    );
  }, [cases, statusFilter, caseSearch]);

  if (flagLoading || eligibilityLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse h-8 w-48 bg-muted rounded" />
      </div>
    );
  }
  if (!enabled) return null;

  // First-run onboarding — if eligibility has come back AND we're not
  // provisioned on Techficient yet, take over the page with the wizard.
  // The eligibility query also drives the small "Setup needed" pill on
  // the normal page, so once the wizard activates the agent the same
  // query result will swap us back automatically.
  if (eligibility && !eligibility.provisioned) {
    return (
      <div className="px-4 md:px-6 pt-10 md:pt-14 pb-12">
        <div className="w-full max-w-2xl mx-auto">
          <QuoteEnrollOnboarding
          defaultAgentName={user?.user?.name}
          defaultEmail={user?.user?.email}
          // Licenses pulled from Techficient when the agent already exists
          // there (MRFG appoints through them) — the wizard opens with their
          // REAL license rows instead of just the NPN default.
          defaultLicenses={
            eligibility.techficientPrefill?.stateLicenses?.length
              ? eligibility.techficientPrefill.stateLicenses
              : undefined
          }
          // Writing-number prefills: roster-staged values first, then
          // Techficient's authoritative numbers WIN per carrier when the
          // agent already exists on their side. Wizard validation still
          // applies before anything reaches Techficient.
          defaultContracts={(() => {
            const merged = new Map<string, string>();
            for (const s of eligibility.stagedContracts || []) {
              if (s.writingNumber) merged.set(s.carrierCode, s.writingNumber);
            }
            for (const c of eligibility.techficientPrefill?.carrierContracts ||
              []) {
              if (c.writingNumber) merged.set(c.carrierCode, c.writingNumber);
            }
            return [...merged.entries()].map(([carrierCode, writingNumber]) => ({
              carrierCode,
              writingNumber,
            }));
          })()}
          // NPN + npnState come from signup (the trial selection form
          // validates the NPN against the state DOI before the account is
          // created). When they're present the wizard skips the Profile
          // step entirely and pre-fills the first license row.
          defaultNpn={(user?.user as any)?.npn || ""}
          defaultNpnState={(user?.user as any)?.npnState || (user?.user as any)?.npn_state || ""}
          onActivated={() => {
            // The wizard already invalidates the eligibility query; this
            // hook is a no-op spot for any future tracking.
          }}
        />
        </div>
      </div>
    );
  }

  // Quote-with-form path — reveals the inline QuoteForm pre-filled from the
  // picked lead (or empty when called with null for a fresh quote).
  const quoteWithForm = (lead: any | null) => {
    setSelectedLead(lead || {});
    setHistoryPreset(null); // fresh pick — drop any re-quote overlay
    setLeadQuery("");
    requestAnimationFrame(() => {
      document
        .getElementById("quote-form-anchor")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  // Re-quote from history: pull the lead (for demographics — DOB fixes and
  // all), overlay the past quote's FULL stored inputs (height/weight/zip
  // aren't on most leads — they were typed into the original form and are
  // stored with the quote), and auto-run so the results (with Apply) appear
  // immediately. Precedence at render: stored quote inputs < fresh lead
  // data < the quote's coverage parameters.
  const requoteFromHistory = async (q: any) => {
    setQuoteHistoryOpen(false);
    let lead: any = null;
    if (q.leadId) {
      try {
        lead = await leadsService.getLead(q.leadId);
      } catch {
        /* lead gone/inaccessible — still requote with the saved params */
      }
    }
    setSelectedLead(lead || {});
    // IMPORTANT: only include keys with real values — a null here would
    // clobber good lead-derived values in the render merge.
    const payloadDefaults: Record<string, any> = {};
    if (q.clientName) payloadDefaults.leadName = q.clientName;
    if (q.state) payloadDefaults.state = q.state;
    if (q.birthDate) payloadDefaults.birthDate = q.birthDate;
    if (q.gender) payloadDefaults.gender = q.gender;
    if (q.zipCode) payloadDefaults.zipCode = q.zipCode;
    if (q.heightInches) payloadDefaults.heightInches = q.heightInches;
    if (q.weightPounds) payloadDefaults.weightPounds = q.weightPounds;
    if (q.phc) payloadDefaults.phcDefaults = q.phc;
    const quoteParams: Record<string, any> = {};
    if (q.leadId) quoteParams.leadId = q.leadId;
    if (q.coverageAmount) quoteParams.faceAmount = Math.round(q.coverageAmount);
    if (q.productType) quoteParams.coverageType = q.productType;
    if (q.termYears) quoteParams.termYears = q.termYears;
    setHistoryPreset({ payloadDefaults, quoteParams });
    setHistoryRunToken((t) => t + 1);
    requestAnimationFrame(() => {
      document
        .getElementById("quote-form-anchor")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  // Quote-with-InsuraBot path — opens the chat with this lead's context so
  // the AI can pre-render the QuoteForm card or just answer questions. Passes
  // a `startQuoting` flag that tells the bot to prefer the quote_form tool
  // over open-ended chat.
  const quoteWithBot = (lead: any | null) => {
    setInsurabotLead(lead || null);
    setInsurabotOpen(true);
    setLeadQuery("");
  };

  // Context payload handed to InsuraBot. Mirrors the lead-detail Quote tab so
  // the AI can auto-fill the quote_form card from DOB/gender/state/etc. The
  // `initialMessage` field is what actually triggers the AI to act — without
  // it the bot just greets the agent and waits.
  const insurabotContext = insurabotLead
    ? {
        leadId: insurabotLead.id,
        leadName:
          insurabotLead.name ||
          `${insurabotLead.firstName ?? ""} ${insurabotLead.lastName ?? ""}`.trim(),
        dob: insurabotLead.dob || insurabotLead.birthday,
        gender: insurabotLead.gender,
        state: insurabotLead.state,
        zipCode: insurabotLead.zipCode || insurabotLead.zip_code,
        height: insurabotLead.height,
        weight: insurabotLead.weight,
        enrollmentMode: true,
        quoteMode: true,
        startQuoting: true,
        autoSendInitial: true,
        initialMessage: `Show me the quote form for ${
          insurabotLead.name ||
          `${insurabotLead.firstName ?? ""} ${insurabotLead.lastName ?? ""}`.trim() ||
          "this lead"
        }, pre-filled with what we have on file.`,
      }
    : {
        enrollmentMode: true,
        quoteMode: true,
        startQuoting: true,
        autoSendInitial: true,
        initialMessage:
          "Show me a blank quote form so I can run a multi-carrier quote.",
      };

  // Date-range filter control (same as main Dashboard) — rendered up in the
  // page header next to "Manage profile".
  const statsFilter = (
    <div className="flex items-center gap-2">
      {statsRange === "custom" && (
        <Popover open={statsCalendarOpen} onOpenChange={setStatsCalendarOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={`w-auto justify-start text-left font-normal h-9 text-xs sm:text-sm ${!statsCalendarRange ? "text-muted-foreground" : ""}`}
            >
              <CalendarIcon className="mr-1.5 h-4 w-4 flex-shrink-0" />
              {statsCalendarRange?.from && statsCalendarRange?.to
                ? `${format(statsCalendarRange.from, "MMM d")} - ${format(statsCalendarRange.to, "MMM d, yyyy")}`
                : "Pick dates"}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-auto p-0" sideOffset={4}>
            <div className="flex flex-col sm:flex-row">
              <div className="border-b sm:border-b-0 sm:border-r p-2 sm:w-36 flex-shrink-0">
                <div className="text-xs font-medium text-muted-foreground px-2 py-1.5 hidden sm:block">
                  Quick Select
                </div>
                <div className="flex flex-wrap sm:flex-col gap-1">
                  {[
                    { label: "Today", from: new Date(), to: new Date() },
                    {
                      label: "Last 7 Days",
                      from: subDays(new Date(), 6),
                      to: new Date(),
                    },
                    {
                      label: "Last 30 Days",
                      from: subDays(new Date(), 29),
                      to: new Date(),
                    },
                    {
                      label: "This Month",
                      from: startOfMonth(new Date()),
                      to: endOfMonth(new Date()),
                    },
                    {
                      label: "Last Month",
                      from: startOfMonth(subMonths(new Date(), 1)),
                      to: endOfMonth(subMonths(new Date(), 1)),
                    },
                    {
                      label: "This Year",
                      from: startOfYear(new Date()),
                      to: new Date(),
                    },
                  ].map((preset) => (
                    <Button
                      key={preset.label}
                      variant="ghost"
                      size="sm"
                      className="justify-start text-xs h-7 px-2 font-normal"
                      onClick={() => {
                        const range = { from: preset.from, to: preset.to };
                        setStatsCalendarRange(range);
                        setStatsCustomRange(range);
                        setStatsCalendarOpen(false);
                      }}
                    >
                      {preset.label}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="p-2">
                <CalendarComponent
                  defaultMonth={statsCalendarRange?.from || new Date()}
                  mode="range"
                  numberOfMonths={2}
                  selected={statsCalendarRange}
                  onSelect={(range) => {
                    setStatsCalendarRange(range);
                    if (range?.from && range?.to) {
                      setStatsCustomRange({ from: range.from, to: range.to });
                      setStatsCalendarOpen(false);
                    }
                  }}
                  className="[&_.rdp-months]:flex-col sm:[&_.rdp-months]:flex-row"
                  showOutsideDays={false}
                />
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}
      <Select
        value={statsRange}
        onValueChange={(v) => {
          setStatsRange(v);
          if (v !== "custom") {
            setStatsCustomRange(undefined);
            setStatsCalendarRange(undefined);
          }
        }}
      >
        <SelectTrigger className="w-36 shadow-sm h-9">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all-time">All Time</SelectItem>
          <SelectItem value="this-month">This Month</SelectItem>
          <SelectItem value="last-month">Last Month</SelectItem>
          <SelectItem value="this-week">This Week</SelectItem>
          <SelectItem value="last-week">Last Week</SelectItem>
          <SelectItem value="custom">Custom Range</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* ─── Header ─── */}
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="p-2.5 rounded-lg bg-primary/10 text-primary">
            <ClipboardCheck className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Quote &amp; Enroll
            </h1>
            <p className="text-sm text-gray-600 mt-0.5">
              Multi-carrier quoting with personalized health classes and in-app
              eApplication enrollment.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {statsFilter}
          {eligibility?.provisioned && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setManageOpen(true)}
              title="Add states / update writing numbers"
            >
              <Settings2 className="h-3.5 w-3.5 mr-1.5" />
              Manage profile
            </Button>
          )}
        </div>
      </header>

      {/* ─── Inline embedded iframe (modal mode) ─── */}
      {floater.open && floater.mode === "modal" && floater.session && (
        <Card
          id="enrollment-embed-anchor"
          className="border-primary/40 shadow-md overflow-hidden scroll-mt-4"
        >
          <div className="flex items-center justify-between gap-3 px-5 py-3 border-b bg-primary/5">
            <div className="flex items-center gap-3 min-w-0">
              <div className="p-1.5 rounded-md bg-primary/10 text-primary flex-shrink-0">
                <FileText className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-500">Application in progress</p>
                <p className="font-semibold text-sm text-gray-900 truncate">
                  {floater.session.carrier || "Carrier"} —{" "}
                  {floater.session.product || "Application"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={floater.popOut}
                title="Pop out — stays open while you move around InsuraCentral"
              >
                <PictureInPicture className="h-3.5 w-3.5 mr-1.5" />
                Pop out
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={floater.close}
                title="Close application"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
          {/* The application iframe is NOT rendered here — it lives in
              GlobalEnrollmentFloater (one iframe, ever) and pins itself
              over this slot. Rendering a second iframe per surface meant
              every pop-out/dock/navigation REMOUNTED the eApp and dropped
              the agent back to the last committed section. */}
          <div id="enrollment-embed-slot" className="bg-white" style={{ height: "78vh" }} />
        </Card>
      )}

      {/* ─── KPI Hero (reuses MetricCard for visual continuity) ─── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          title="Apps Started"
          value={kpis.total.toString()}
          subtitle={statsWindow.label}
          icon={TrendingUp}
          accent="blue"
          trend="neutral"
        />
        <MetricCard
          title="In Progress"
          value={kpis.inProgress.toString()}
          subtitle="Drafting + underwriting"
          icon={Clock}
          accent="amber"
          trend="up"
        />
        <MetricCard
          title="Approved"
          value={kpis.approvedThisMonth.toString()}
          subtitle={statsWindow.label}
          icon={CheckCircle2}
          accent="emerald"
          trend="up"
        />
        <MetricCard
          title="Premium sold"
          value={fmtMoney(kpis.premiumLocked)}
          subtitle={`Annualized · ${statsWindow.label.toLowerCase()}`}
          icon={DollarSign}
          accent="violet"
          trend="up"
        />
      </div>

      {/* ─── Commission ledger KPIs ─── */}
      {commissionBalance && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MetricCard
            title="Advanced"
            value={fmtMoney(commissionBalance.advanced)}
            subtitle="Carrier paid out · in chargeback window"
            icon={DollarSign}
            trend="up"
          />
          <MetricCard
            title="Charged back"
            value={fmtMoneyNegative(commissionBalance.chargedBack)}
            subtitle="Lapses / NTOs / cancels"
            icon={AlertCircle}
            trend="down"
          />
          <MetricCard
            title="Earned"
            value={fmtMoney(commissionBalance.earned)}
            subtitle="Past chargeback window · locked in"
            icon={Coins}
            accent="emerald"
            trend="up"
          />
          <MetricCard
            title="Net in pocket"
            value={fmtMoney(commissionBalance.net)}
            subtitle="Advanced + Earned − Charged back"
            icon={Wallet}
            accent="violet"
            trend="up"
          />
        </div>
      )}

      {/* ─── Lead picker → inline form ─── */}
      <div className="grid grid-cols-1 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-4 w-4 text-primary" />
                {selectedLead ? "Quote details" : "Pick a lead to quote"}
              </CardTitle>
              <div className="flex items-center gap-1.5">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setQuoteHistoryOpen((v) => !v)}
                  className={
                    quoteHistoryOpen
                      ? "text-primary"
                      : "text-gray-500 hover:text-gray-900"
                  }
                  title="See your recent quotes"
                >
                  <History className="h-3.5 w-3.5 mr-1" />
                  Quote history
                </Button>
                {selectedLead && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setSelectedLead(null)}
                  >
                    <X className="h-3.5 w-3.5 mr-1" />
                    Change lead
                  </Button>
                )}
              </div>
            </div>

            {/* ─── Quote history — inline, buttery expand (grid-rows trick
                animates height smoothly without measuring). Click a row to
                re-run that quote with every original input; the arrow icon
                jumps to the lead's details instead. ─── */}
            <div
              className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out ${
                quoteHistoryOpen
                  ? "grid-rows-[1fr] opacity-100 mt-3"
                  : "grid-rows-[0fr] opacity-0"
              }`}
            >
              <div className="overflow-hidden min-h-0">
                <div className="rounded-lg border bg-gray-50/60 p-3">
                  <div className="flex items-center gap-2 mb-2.5">
                    <div className="relative flex-1">
                      <Search className="h-3.5 w-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                      <Input
                        value={historySearch}
                        onChange={(e) => setHistorySearch(e.target.value)}
                        placeholder="Search past quotes — name, state, product…"
                        className="h-8 pl-8 text-sm bg-white"
                      />
                    </div>
                    <p className="text-[10px] text-gray-400 hidden sm:block flex-shrink-0">
                      Click to re-run · arrow opens the lead
                    </p>
                  </div>
                  {quoteHistoryLoading ? (
                    <div className="space-y-2">
                      <div className="animate-pulse h-12 bg-white rounded-md" />
                      <div className="animate-pulse h-12 bg-white rounded-md" />
                      <div className="animate-pulse h-12 bg-white rounded-md" />
                    </div>
                  ) : (
                    (() => {
                      const qy = historySearch.trim().toLowerCase();
                      const filtered = qy
                        ? quoteHistory.filter((q) =>
                            [
                              q.clientName,
                              q.state,
                              q.productType,
                              q.coverageAmount != null
                                ? String(Math.round(q.coverageAmount))
                                : null,
                            ]
                              .filter(Boolean)
                              .join(" ")
                              .toLowerCase()
                              .includes(qy),
                          )
                        : quoteHistory;
                      if (filtered.length === 0) {
                        return (
                          <div className="text-center py-8">
                            <History className="h-7 w-7 text-gray-300 mx-auto mb-2" />
                            <p className="text-sm font-medium text-gray-700">
                              {qy ? "No quotes match" : "No quotes yet"}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              {qy
                                ? "Try a different name or state."
                                : "Quotes you run will show up here."}
                            </p>
                          </div>
                        );
                      }
                      return (
                        <div className="divide-y border rounded-md overflow-hidden bg-white max-h-72 overflow-y-auto">
                          {filtered.map((q) => {
                            const who =
                              q.clientName ||
                              (q.leadId ? "Saved lead" : "Quick quote");
                            return (
                              <div
                                key={q.id}
                                role="button"
                                tabIndex={0}
                                onClick={() => void requoteFromHistory(q)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter")
                                    void requoteFromHistory(q);
                                }}
                                className="w-full text-left px-3 py-2.5 flex items-center gap-3 hover:bg-gray-50 cursor-pointer"
                              >
                                <RefreshCw className="h-3.5 w-3.5 text-gray-300 flex-shrink-0" />
                                <div className="min-w-0 flex-1">
                                  <p className="font-medium text-sm text-gray-900 truncate">
                                    {who}
                                    {q.state ? (
                                      <span className="text-gray-400 font-normal">
                                        {" "}
                                        · {q.state}
                                      </span>
                                    ) : null}
                                  </p>
                                  <p className="text-xs text-gray-500 truncate mt-0.5">
                                    {q.coverageAmount
                                      ? `$${Math.round(q.coverageAmount).toLocaleString()}`
                                      : "—"}
                                    {q.productType
                                      ? ` · ${q.productType === "FinalExpense" ? "Final Expense" : q.productType}`
                                      : ""}
                                    {q.termYears ? ` · ${q.termYears}yr` : ""}
                                    {" · "}
                                    {new Date(q.createdAt).toLocaleDateString(
                                      "en-US",
                                      { month: "short", day: "numeric" },
                                    )}
                                  </p>
                                </div>
                                <div className="text-right flex-shrink-0">
                                  <p className="text-sm font-semibold text-gray-900">
                                    {q.bestMonthlyPremium != null
                                      ? `$${q.bestMonthlyPremium.toFixed(2)}/mo`
                                      : "—"}
                                  </p>
                                  <p className="text-[11px] text-gray-500">
                                    {q.carrierCount} carrier
                                    {q.carrierCount === 1 ? "" : "s"}
                                  </p>
                                </div>
                                {q.leadId && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      router.push(window.location.pathname.startsWith("/m") ? `/m/leads/${q.leadId}` : `/leads/${q.leadId}`);
                                    }}
                                    className="p-1.5 rounded-md text-gray-400 hover:text-primary hover:bg-primary/5 flex-shrink-0"
                                    title="Open lead details"
                                  >
                                    <ExternalLink className="h-3.5 w-3.5" />
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()
                  )}
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div id="quote-form-anchor" />
            {selectedLead ? (
              <QuoteForm
                key={`${selectedLead?.id || "fresh"}-${historyRunToken}`}
                preset={(() => {
                  // Null-safe merge, lowest → highest precedence: the
                  // quote's stored inputs fill gaps (height/weight/zip),
                  // FRESH lead data wins demographics (DOB fixes!), and
                  // the quote's coverage params are authoritative.
                  const merged: Record<string, any> = {
                    ...(historyPreset?.payloadDefaults || {}),
                  };
                  for (const [k, v] of Object.entries(
                    leadToPreset(selectedLead) as Record<string, any>,
                  )) {
                    if (v !== null && v !== undefined && v !== "") merged[k] = v;
                  }
                  Object.assign(merged, historyPreset?.quoteParams || {});
                  return merged;
                })()}
                autoRun={!!historyPreset}
              />
            ) : (
              <>
                {/* Search row */}
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
                  <Input
                    placeholder="Search by name, phone, or email…"
                    value={leadQuery}
                    onChange={(e) => setLeadQuery(e.target.value)}
                    className="pl-9 h-10"
                  />
                  {leadsLoading && (
                    <Loader2 className="absolute right-3 top-3 h-4 w-4 animate-spin text-gray-400" />
                  )}
                </div>

                {trimmedQuery.length >= 2 ? (
                  <div className="mt-3 border rounded-md bg-white divide-y max-h-80 overflow-y-auto">
                    {leads.length === 0 && !leadsLoading ? (
                      <p className="text-sm text-gray-500 px-4 py-6 text-center">
                        No leads match &ldquo;{trimmedQuery}&rdquo;.
                      </p>
                    ) : (
                      leads.slice(0, 6).map((l: any) => {
                        const name =
                          l.name ||
                          `${l.firstName ?? ""} ${l.lastName ?? ""}`.trim() ||
                          l.email ||
                          l.phone ||
                          "Unnamed lead";
                        const initials = getInitials(name);
                        return (
                          <div
                            key={l.id}
                            className="px-3 py-2.5 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                          >
                            <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold flex-shrink-0">
                              {initials}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-sm text-gray-900 truncate">
                                {name}
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                {formatPhone(l.phone) || l.email || "—"}
                                {l.state ? ` · ${l.state}` : ""}
                              </p>
                            </div>
                            {/* Per-lead action chips — agent picks how they
                                want to quote this prospect. Form is faster /
                                self-serve; InsuraBot is better for tricky
                                cases where they want the AI's nudge. */}
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              <Button
                                size="sm"
                                onClick={() => quoteWithForm(l)}
                                className="h-8"
                              >
                                <Calculator className="h-3.5 w-3.5 mr-1.5" />
                                Quote
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => quoteWithBot(l)}
                                className="h-8 px-2"
                                title="Open InsuraBot for this lead"
                              >
                                <InsuraBotIcon className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                ) : (
                  <>
                    <p className="text-xs text-gray-500 mt-2">
                      Type at least 2 characters to search your leads.
                    </p>

                    {/* No-lead path — when the prospect isn't in the CRM yet
                        or the agent just wants to test rates. Two parallel
                        buttons mirror the per-lead chips above so the UX is
                        consistent. */}
                    <div className="mt-5 rounded-lg border border-dashed border-gray-200 p-4">
                      <p className="text-xs font-semibold text-gray-700 mb-3">
                        Or skip the lead and start fresh
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <Button
                          onClick={() => quoteWithForm(null)}
                          className="h-10"
                        >
                          <Calculator className="h-4 w-4 mr-1.5" />
                          Quote with form
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => quoteWithBot(null)}
                          className="h-10 gap-1.5"
                        >
                          <InsuraBotIcon className="h-4 w-4" />
                          Quote with InsuraBot
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ─── Applications & Cases ─── */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-4 w-4 text-gray-600" />
                Applications &amp; Cases
              </CardTitle>
              <p className="text-xs text-gray-500 mt-0.5">
                Statuses refresh every few minutes — hit Sync to pull now.
              </p>
            </div>
            <div className="flex items-center gap-1 flex-wrap">
              <Button
                size="sm"
                variant="ghost"
                onClick={syncNow}
                disabled={syncing}
                className="h-7 px-2 text-xs text-gray-500 hover:text-gray-900"
                title="Pull latest case statuses from the carrier"
              >
                <RefreshCw
                  className={`h-3.5 w-3.5 mr-1 ${syncing ? "animate-spin" : ""}`}
                />
                {syncing ? "Syncing…" : "Sync"}
              </Button>
              <FilterPill
                label="All"
                count={cases.length}
                active={statusFilter === "all"}
                onClick={() => setStatusFilter("all")}
              />
              <FilterPill
                label="In progress"
                color="blue"
                count={
                  cases.filter(
                    (c) => caseBucket(c) === "in_progress",
                  ).length
                }
                active={statusFilter === "in_progress"}
                onClick={() => setStatusFilter("in_progress")}
              />
              <FilterPill
                label="Underwriting"
                color="amber"
                count={
                  cases.filter(
                    (c) => caseBucket(c) === "submitted",
                  ).length
                }
                active={statusFilter === "submitted"}
                onClick={() => setStatusFilter("submitted")}
              />
              <FilterPill
                label="Approved"
                color="emerald"
                count={
                  cases.filter(
                    (c) => caseBucket(c) === "approved",
                  ).length
                }
                active={statusFilter === "approved"}
                onClick={() => setStatusFilter("approved")}
              />
              <FilterPill
                label="Closed"
                color="rose"
                count={
                  cases.filter(
                    (c) => caseBucket(c) === "declined",
                  ).length
                }
                active={statusFilter === "declined"}
                onClick={() => setStatusFilter("declined")}
              />
            </div>
          </div>
          {/* Search across the ledger — name, carrier, product, status,
              policy #, face amount. Only shown once there's something to
              search. */}
          {cases.length > 0 && (
            <div className="relative mt-3">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
              <Input
                placeholder="Search applications — name, carrier, status, policy #…"
                value={caseSearch}
                onChange={(e) => setCaseSearch(e.target.value)}
                className="pl-9 h-9 text-sm"
              />
              {caseSearch && (
                <button
                  type="button"
                  onClick={() => setCaseSearch("")}
                  className="absolute right-2.5 top-2 text-gray-400 hover:text-gray-700 p-0.5"
                  title="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
        </CardHeader>
        <CardContent className="pt-0">
          {casesLoading ? (
            <div className="space-y-2 py-2">
              <div className="animate-pulse h-14 bg-gray-100 rounded-md" />
              <div className="animate-pulse h-14 bg-gray-100 rounded-md" />
            </div>
          ) : filteredCases.length === 0 ? (
            <div className="text-center py-10 border-t">
              <FileText className="h-8 w-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm font-medium text-gray-700">
                {caseSearch.trim()
                  ? `No applications match “${caseSearch.trim()}”`
                  : statusFilter === "all"
                    ? "No applications yet"
                    : "No cases in this status"}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {caseSearch.trim()
                  ? "Try a different name, carrier, or status."
                  : statusFilter === "all"
                    ? "Quote a lead above to begin enrolling."
                    : "Try changing the filter."}
              </p>
            </div>
          ) : (
            <div
              className="divide-y border-t max-h-[560px] overflow-y-auto pr-1"
              style={{ scrollbarWidth: "thin" }}
            >
              {filteredCases.map((c) => {
                const bucket = caseBucket(c);
                const progress = progressForCase(c);
                return (
                  <div
                    key={c.id}
                    className="py-3 flex items-start gap-3 hover:bg-gray-50/60 rounded-md -mx-2 px-2 transition-colors"
                  >
                    <CarrierLogo name={c.carrierName} size={32} />
                    <div className="min-w-0 flex-1">
                      {/* Status badge sits on TOP, above the client — reads
                          as the row's header, not a side chip. */}
                      <div className="mb-1">
                        <StatusPill bucket={bucket} />
                      </div>
                      <p className="font-medium text-sm text-gray-900 truncate">
                        {/* Lead name FIRST — agents scan by client, and two
                            same-carrier cases are indistinguishable without
                            it. Clickable straight into the lead. */}
                        {c.leadName && (
                          <>
                            <button
                              type="button"
                              onClick={() =>
                                c.leadId && router.push(window.location.pathname.startsWith("/m") ? `/m/leads/${c.leadId}` : `/leads/${c.leadId}`)
                              }
                              className="text-primary hover:underline font-semibold"
                              title="Open lead details"
                            >
                              {c.leadName}
                            </button>
                            <span className="text-gray-400"> · </span>
                          </>
                        )}
                        {c.carrierName || "Carrier"} —{" "}
                        {c.productName || "Product"}
                      </p>
                      {/* Progress bar only while the application is still
                          moving. Approved / Closed are terminal — the pill
                          already says it. */}
                      {progress &&
                        bucket !== "approved" &&
                        bucket !== "declined" && (
                          <div className="mt-1.5 flex items-center gap-2">
                            <div className="flex-1 max-w-[180px] h-1 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className={`h-full transition-all duration-500 ${progress.colorClass}`}
                                style={{ width: `${progress.percent}%` }}
                              />
                            </div>
                            <span className="text-[10px] text-gray-500">
                              {progress.label}
                            </span>
                          </div>
                        )}

                      {/* Everything as aligned badges: coverage (labeled so
                          it's clear it's the death benefit), rate class,
                          policy #, commission. The status is already the pill
                          on top, so it isn't repeated here. */}
                      {(() => {
                        const applied =
                          c.appliedHealthClassName || c.appliedHealthClassCode;
                        const approved =
                          c.approvedHealthClassName || c.approvedHealthClassCode;
                        const isDowngrade = !!(
                          applied &&
                          approved &&
                          applied !== approved
                        );
                        const cleanClass = !isDowngrade
                          ? approved || applied
                          : null;
                        const hasFace =
                          Number.isFinite(Number(c.appliedFaceAmount)) &&
                          Number(c.appliedFaceAmount) > 0;
                        if (
                          !hasFace &&
                          !cleanClass &&
                          !c.policyNumber &&
                          !c.commission
                        )
                          return null;
                        return (
                          <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                            {hasFace && (
                              <span className="inline-flex items-center gap-1 rounded-md bg-gray-100 border border-gray-200 px-2 py-0.5 text-[11px]">
                                <span className="text-gray-500">Coverage</span>
                                <span className="font-bold text-gray-900 tabular-nums">
                                  {fmtMoney(c.appliedFaceAmount)}
                                </span>
                              </span>
                            )}
                            {cleanClass && (
                              <span className="inline-flex items-center rounded-md bg-gray-100 border border-gray-200 px-2 py-0.5 text-[11px] font-medium text-gray-700">
                                {cleanClass}
                              </span>
                            )}
                            {c.policyNumber && (
                              <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 border border-emerald-200 px-2 py-0.5">
                                <FileText className="h-3 w-3 text-emerald-600" />
                                <span className="text-[11px] font-semibold text-emerald-800 tabular-nums">
                                  #{c.policyNumber}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    navigator.clipboard
                                      ?.writeText(String(c.policyNumber))
                                      .then(() =>
                                        toast.success("Policy number copied"),
                                      )
                                      .catch(() => {});
                                  }}
                                  className="ml-0.5 text-emerald-600/70 hover:text-emerald-800"
                                  title="Copy policy number"
                                >
                                  <ClipboardCheck className="h-3 w-3" />
                                </button>
                              </span>
                            )}
                            {c.commission && (
                              <CommissionBadge commission={c.commission} />
                            )}
                          </div>
                        );
                      })()}

                      {/* Only the meaningful underwriting story — a rate-class
                          DOWNGRADE or an underwriter reason. Clean approvals
                          show their class inline in the meta line above. */}
                      {(() => {
                        const applied =
                          c.appliedHealthClassName || c.appliedHealthClassCode;
                        const approved =
                          c.approvedHealthClassName || c.approvedHealthClassCode;
                        const isDowngrade = !!(
                          applied &&
                          approved &&
                          applied !== approved
                        );
                        return isDowngrade || c.underwritingDecisionReason ? (
                          <UnderwritingDecisionRow caseRow={c} />
                        ) : null;
                      })()}
                    </div>
                    {c.launchUrl && c.resumable === false && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-shrink-0 self-center gap-1.5"
                        onClick={() => viewCase(c)}
                        title="View the submitted application (read-only)"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                        View
                      </Button>
                    )}
                    {c.launchUrl && c.resumable !== false && (
                      <div className="flex gap-1.5 flex-shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            // This case's carrier window is still open →
                            // focus it. A fresh resume URL would restart
                            // the carrier session from page one.
                            if (
                              floater.popupOpen &&
                              floater.popupSession?.caseId ===
                                c.techficientCaseId
                            ) {
                              floater.focusPopup();
                              return;
                            }
                            // Resume ALWAYS fetches a fresh URL (stored one
                            // is the stale initiate-time techf link; carrier-
                            // stage cases resume on the carrier's site). The
                            // popup blocker only allows window.open inside
                            // the click gesture, so we pre-reserve a popup —
                            // but ONLY when this case is predicted to need
                            // one (launchMode hint). Iframe-predicted cases
                            // go straight to the embed with no popup flash.
                            const expectPopup = c.launchMode === "popup";
                            if (expectPopup && !floater.openPendingPopup()) {
                              toast.error("Popup blocked", {
                                description:
                                  "Your browser blocked the application window. Allow popups for this site, then click Resume again.",
                              });
                              return;
                            }
                            try {
                              const fresh = await techficientService.resumeEapp(
                                c.techficientCaseId,
                              );
                              const session = {
                                caseId: c.techficientCaseId,
                                launchUrl: fresh.launchUrl,
                                carrier: fresh.carrier || c.carrierName,
                                product: fresh.product || c.productName,
                              };
                              if (fresh.launchMode === "popup") {
                                if (expectPopup) {
                                  floater.completePendingPopup({
                                    ...session,
                                    launchMode: "popup",
                                  });
                                } else {
                                  // Prediction missed (case advanced to the
                                  // carrier stage since the list loaded).
                                  // We're past the click gesture, so a
                                  // direct popup would be blocked — hand
                                  // the agent a one-click opener instead of
                                  // failing silently.
                                  const ok = floater.launch({
                                    ...session,
                                    launchMode: "popup",
                                  });
                                  if (!ok) {
                                    toast("Signature step is ready", {
                                      description: `${session.carrier || "The carrier"} needs to open in its own window.`,
                                      action: {
                                        label: "Open",
                                        onClick: () =>
                                          floater.launch({
                                            ...session,
                                            launchMode: "popup",
                                          }),
                                      },
                                      duration: 15000,
                                    });
                                    return;
                                  }
                                }
                                // Keep the Techficient case view embedded
                                // in-app behind the carrier popup — the two
                                // surfaces are independent.
                                if (c.launchUrl) {
                                  floater.launch({
                                    caseId: c.techficientCaseId,
                                    launchUrl: c.launchUrl,
                                    carrier: c.carrierName,
                                    product: c.productName,
                                  });
                                }
                              } else {
                                if (expectPopup) floater.cancelPendingPopup();
                                floater.launch(session);
                              }
                            } catch (e: any) {
                              if (expectPopup) floater.cancelPendingPopup();
                              const st = e?.response?.status ?? e?.status;
                              const cd =
                                e?.response?.data?.code ?? e?.code;
                              if (st === 409 || cd === "CASE_NOT_RESUMABLE") {
                                toast("Application already submitted", {
                                  description:
                                    "This one's been sent to the carrier and can no longer be reopened.",
                                });
                                return;
                              }
                              toast.error("Couldn't resume application", {
                                description:
                                  e?.message ||
                                  "Fetching a fresh application link failed — try again.",
                              });
                            }
                          }}
                        >
                          <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                          Resume
                        </Button>
                        {/* Float only applies to embedded iframes — a popup
                            already IS its own window. */}
                        {c.launchMode !== "popup" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              floater.launch(
                                {
                                  caseId: c.techficientCaseId,
                                  launchUrl: c.launchUrl!,
                                  carrier: c.carrierName,
                                  product: c.productName,
                                },
                                { floating: true },
                              )
                            }
                            title="Open as a floating window — stays put across pages"
                          >
                            <PictureInPicture className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ─── InsuraBot ─── */}
      <InsuraBot
        open={insurabotOpen}
        onOpenChange={(v) => {
          setInsurabotOpen(v);
          if (!v) setInsurabotLead(null);
        }}
        defaultContext="enrollment"
        contextMetadata={insurabotContext}
      />

      {/* ─── Manage producer profile modal — dedicated ManageProfile
          dashboard (KPI tiles + User Info + Licenses + Carriers + Save).
          Distinct from the first-run wizard; opens whenever the agent
          clicks "Manage profile" in the header. ─── */}
      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent className="manage-profile-dialog sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage producer profile</DialogTitle>
          </DialogHeader>
          {eligibility && (
            <ManageProfile
              eligibility={eligibility}
              onClose={() => setManageOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* ─── Onboarding modal (first visit only) ─── */}
      <Dialog
        open={onboardingOpen}
        onOpenChange={(v) => (v ? setOnboardingOpen(true) : dismissOnboarding())}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Rocket className="h-5 w-5 text-primary" />
              Welcome to Quote &amp; Enroll
            </DialogTitle>
            <DialogDescription>
              Three steps to enroll a client end-to-end.
            </DialogDescription>
          </DialogHeader>
          <ol className="space-y-3 py-2">
            <OnboardingStep
              num={1}
              title="Pick a lead from your CRM"
              body="Search by name, phone, or email. The quote form fills itself in with everything we have on file."
            />
            <OnboardingStep
              num={2}
              title="Fill in what's missing & run"
              body="Coverage amount, term length, a few health questions if you want better rates. Hit Run — every carrier you're appointed with ranks itself by price."
            />
            <OnboardingStep
              num={3}
              title="Apply — the eApp opens right here"
              body="Click Apply on any carrier card. The application loads embedded in this page. Pop it out to keep it floating during calls."
            />
          </ol>
          <div className="bg-primary/5 border border-primary/15 rounded-md p-3 text-xs text-primary/90 flex items-start gap-2">
            <Eye className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
            <span>
              <strong>Pro tip:</strong> the floating window can be made
              translucent — click the eye icon to cycle through see-through
              modes.
            </span>
          </div>
          <div className="flex justify-end pt-1">
            <Button onClick={dismissOnboarding}>Got it — let&apos;s go</Button>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}

/* ──────── helpers + small components ──────── */

function fmtMoney(n: unknown): string {
  const x = Number(n);
  const safe = Number.isFinite(x) ? x : 0;
  const abs = Math.round(Math.abs(safe));
  return `${safe < 0 ? "-" : ""}$${abs.toLocaleString()}`;
}

function fmtMoneyNegative(n: unknown): string {
  const x = Math.round(Math.abs(Number(n)));
  const safe = Number.isFinite(x) ? x : 0;
  return safe === 0 ? "$0" : `-$${safe.toLocaleString()}`;
}

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || "")
    .join("");
}

/**
 * Format a CRM phone number for display. Strips +1 / country code, drops
 * non-digits, and renders the last 10 as `(XXX) XXX-XXXX`. Falls back to the
 * raw string for malformed input — better an ugly number than no phone.
 */
function formatPhone(raw: unknown): string {
  if (!raw) return "";
  const digits = String(raw).replace(/\D+/g, "");
  const ten = digits.length === 11 && digits.startsWith("1")
    ? digits.slice(1)
    : digits.slice(-10);
  if (ten.length !== 10) return String(raw);
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
}

type StatusBucket =
  | "in_progress"
  | "submitted"
  | "approved"
  | "declined"
  | "unknown";

// Bucket a case by its REAL status. `caseStatus` is a coarse lifecycle
// enum ("initiated" for every drafting/abandoned/submitted case), so the
// human `caseStatusName` ("Application Abandoned", "Application Submitted
// To Carrier") is authoritative — reading only caseStatus made everything
// show "Draft / Drafting 25%". Falls back to caseStatus when no name.
function caseBucket(c: any): StatusBucket {
  return bucketForStatus(c?.caseStatusName || c?.caseStatus);
}

function bucketForStatus(status?: string | null): StatusBucket {
  const s = (status || "").toLowerCase();
  if (!s) return "unknown";
  if (
    s.includes("approved") ||
    s.includes("active") ||
    s.includes("issued") ||
    s.includes("inforce")
  ) {
    return "approved";
  }
  if (
    s.includes("declined") ||
    s.includes("denied") ||
    s.includes("rejected") ||
    s.includes("cancelled") ||
    s.includes("canceled") ||
    s.includes("withdrawn") ||
    s.includes("lapsed") ||
    s.includes("abandoned") ||
    s.includes("not taken") ||
    s.includes("expired") ||
    s.includes("surrender") ||
    s.includes("terminated") ||
    s.includes("closed")
  ) {
    return "declined";
  }
  // Underwriting bucket — the application has LEFT the agent's hands and is
  // with the carrier. NOTE: "Application In Progress" is the agent still
  // DRAFTING the eApp (pre-submission) — that is NOT underwriting, so we do
  // NOT match a bare "progress" here (that mis-bucketed drafting cases as
  // UW). Only carrier-side signals count: submitted / underwriting /
  // pending / in review.
  if (
    s.includes("submit") ||
    s.includes("underwriting") ||
    s.includes("pending") ||
    s.includes("in review") ||
    s.includes("carrier review")
  ) {
    return "submitted";
  }
  // Everything else (Not Started, Initiated, Application In Progress, Draft)
  // is the agent still working the application → In Progress.
  return "in_progress";
}

// The carrier's own furthest-page progress for a still-open application.
// Techficient returns furthestPagePercentage on the case (exposed via
// `raw`); we surface it so an agent sees "60% complete" and knows their
// work is saved — even though Techficient's own eApp reopens Resume at
// page 1 (their redirect, which we can't change from a cross-origin
// iframe). Null when not applicable/available.
function furthestPct(c: any): number | null {
  const p = Number(c?.raw?.furthestPagePercentage);
  return Number.isFinite(p) && p > 0 && p <= 100 ? Math.round(p) : null;
}

function progressForCase(
  c: any,
): { percent: number; label: string; colorClass: string } | null {
  const bucket = caseBucket(c);
  const real = furthestPct(c);
  switch (bucket) {
    case "in_progress":
      return {
        // Use the carrier's real furthest-page % when we have it, so the
        // bar reflects actual progress instead of a flat 25%.
        percent: real ?? 25,
        label: real != null ? `${real}% complete` : "Drafting",
        // Brand-bound — uses the white-label primary color so MRFG,
        // Direct130, etc. each show their own brand on "in progress" rows
        // rather than a generic blue.
        colorClass: "bg-primary/80",
      };
    case "submitted":
      return {
        percent: real ?? 65,
        label: real != null ? `${real}% complete` : "Underwriting",
        colorClass: "bg-amber-400",
      };
    case "approved":
      return { percent: 100, label: "Issued", colorClass: "bg-emerald-500" };
    case "declined":
      return { percent: 100, label: "Closed", colorClass: "bg-rose-400" };
    default:
      return null;
  }
}

function FilterPill({
  label,
  count,
  active,
  color,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  color?: "blue" | "amber" | "emerald" | "rose";
  onClick: () => void;
}) {
  const activeColorMap: Record<NonNullable<typeof color> | "default", string> = {
    // `blue` slot maps to the brand primary so white-label tenants see
    // their own color on the "In progress" filter. Other slots remain
    // semantic (amber = underwriting, emerald = approved, rose = closed).
    blue: "bg-primary/15 text-primary border-primary/30",
    amber: "bg-amber-100 text-amber-800 border-amber-200",
    emerald: "bg-emerald-100 text-emerald-700 border-emerald-200",
    rose: "bg-rose-100 text-rose-700 border-rose-200",
    default: "bg-primary text-white border-primary",
  };
  const activeStyle = active ? activeColorMap[color || "default"] : "";
  return (
    <button
      onClick={onClick}
      className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
        active
          ? activeStyle
          : "bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:text-gray-900"
      }`}
    >
      {label}
      <span className="ml-1 opacity-70">({count})</span>
    </button>
  );
}

function StatusPill({ bucket }: { bucket: StatusBucket }) {
  const map: Record<
    StatusBucket,
    {
      label: string;
      classes: string;
      Icon: React.ComponentType<{ className?: string }>;
    }
  > = {
    in_progress: {
      label: "In progress",
      classes: "bg-primary/10 text-primary border-primary/20",
      Icon: Clock,
    },
    submitted: {
      label: "UW",
      classes: "bg-amber-50 text-amber-800 border-amber-200",
      Icon: Clock,
    },
    approved: {
      label: "Approved",
      classes: "bg-emerald-50 text-emerald-700 border-emerald-200",
      Icon: CheckCircle2,
    },
    declined: {
      label: "Closed",
      classes: "bg-rose-50 text-rose-700 border-rose-200",
      Icon: XCircle,
    },
    unknown: {
      label: "—",
      classes: "bg-gray-50 text-gray-600 border-gray-200",
      Icon: Clock,
    },
  };
  const { label, classes, Icon } = map[bucket];
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border flex-shrink-0 mt-0.5 ${classes}`}
    >
      <Icon className="h-2.5 w-2.5" />
      {label}
    </span>
  );
}

function UnderwritingDecisionRow({ caseRow }: { caseRow: any }) {
  const applied =
    caseRow.appliedHealthClassName || caseRow.appliedHealthClassCode || null;
  const approved =
    caseRow.approvedHealthClassName || caseRow.approvedHealthClassCode || null;
  const isDowngrade = !!(applied && approved && applied !== approved);
  const reason = caseRow.underwritingDecisionReason;
  return (
    <div className="mt-1.5 flex items-start gap-2 flex-wrap">
      <span
        className={`inline-flex items-center gap-1 text-[10px] font-semibold rounded px-1.5 py-0.5 ${
          isDowngrade
            ? "bg-amber-100 text-amber-800 border border-amber-200"
            : "bg-emerald-50 text-emerald-700 border border-emerald-200"
        }`}
      >
        {applied && approved && applied !== approved
          ? `Applied ${applied} → ${approved}`
          : `Class: ${approved || applied}`}
      </span>
      {reason && (
        <span
          className="text-[10px] text-gray-600 italic line-clamp-2 max-w-md"
          title={reason}
        >
          &ldquo;{reason}&rdquo;
        </span>
      )}
    </div>
  );
}

function OnboardingStep({
  num,
  title,
  body,
}: {
  num: number;
  title: string;
  body: string;
}) {
  return (
    <li className="flex gap-3">
      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center mt-0.5">
        {num}
      </span>
      <div>
        <p className="font-medium text-sm text-gray-900">{title}</p>
        <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">{body}</p>
      </div>
    </li>
  );
}
